// src/App.js
import React, { useEffect, useState } from "react";
import {
  Routes,
  Route,
  NavLink,
  useLocation,
  useNavigate,
  Navigate,
} from "react-router-dom";
import { useTranslation } from "react-i18next";

import MainLayout from "./layouts/MainLayout";
import AboutUs from "./pages/client/about/AboutUs";
import Cruise from "./pages/client/services/Cruise";
import EmploymentMain from "./pages/client/services/EmploymentMain";
import ServiceGateway from "./pages/client/home/ServiceGateway";
import ContactUs from "./pages/client/contact/ContactUs";
import HtmlLang from "./components/HtmlLang";

import "./App.css";
import QnA from "./pages/client/faq/Faq";
import Inquiry from "./pages/client/contact/Inquiry";
import InquiryWrite from "./pages/client/contact/Write";
import InquiryDetail from "./pages/client/contact/InquiryDetail";
import InquiryEdit from "./pages/client/contact/InquiryEdit";
import AdminDashboard from "./pages/admin/Dashboard";
import ContactList from "./pages/admin/contact/ContactList";
import ContactDetail from "./pages/admin/contact/ContactDetail";
import AdminLogin from "./pages/admin/Login";
import AdminRegister from "./pages/admin/Register";
import AdminLayout from "./layouts/admin/AdminLayout";
import UserList from "./pages/admin/user/UserList";
import UserDetail from "./pages/admin/user/UserDetail";
import NewsList from "./pages/client/news/NewsList";
import NewsCreate from "./pages/admin/news/NewsCreate";
import NewsDetail from "./pages/client/news/NewsDetail";
import AdminNewsDetail from "./pages/admin/news/NewsDetail";
import AdminNewsList from "./pages/admin/news/NewsList";
import Employment from "./pages/client/employment/Employment";
import EmploymentDetail from "./pages/client/employment/EmploymentDetail";
import EmploymentCreate from "./pages/admin/employment/EmploymentCreate";
import EmploymentList from "./pages/admin/employment/EmploymentList";
import AdminEmploymentDetail from "./pages/admin/employment/EmploymentDetail";
import AdminEmploymentList from "./pages/admin/employment/EmploymentList";
import EmploymentInquiry from "./pages/client/employment/EmploymentInquiry";
import EmploymentInquiryList from "./pages/admin/employment/EmploymentInquiryList";
import EmploymentInquiryDetail from "./pages/admin/employment/EmploymentInquiryDetail";

function ScrollToTopAndHash() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [pathname]);
  useEffect(() => {
    if (!hash) return;
    const id = hash.replace("#", "");
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [hash]);
  return null;
}

function NotFound() {
  const { t } = useTranslation();
  return (
    <div className="mx-auto max-w-3xl px-4 py-20 text-center">
      <h1 className="text-2xl font-bold">
        {t("common.notFoundTitle", {
          defaultValue: "페이지를 찾을 수 없습니다",
        })}
      </h1>
      <NavLink
        to="/"
        className="mt-6 inline-flex rounded-xl border px-4 py-2 text-sm"
      >
        {t("common.backHome", { defaultValue: "메인으로" })}
      </NavLink>
    </div>
  );
}

export default function App() {
  const [alertMessage, setAlertMessage] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();

  // 관리자 접근 보호
  const ProtectedAdminRoute = ({ children }) => {
    // 세션에서 사용자 정보 가져오기
    const storedUser = sessionStorage.getItem("user");
    const user = storedUser ? JSON.parse(storedUser) : null;

    // 로그인 확인
    if (!user) {
      if (!alertMessage) setAlertMessage("로그인 후 사용해주세요.");
      return <Navigate to="/login" state={{ from: location }} />;
    }

    // 권한 확인
    if (user.userType !== "ADMIN") {
      if (!alertMessage) {
        setAlertMessage("접근권한이 없는 계정입니다.");
      }
      return <Navigate to="/" />;
    }

    return children;
  };

  return (
    <>
      <HtmlLang />
      <ScrollToTopAndHash />
      <Routes>
        <Route
          path="/"
          element={
            <MainLayout>
              <ServiceGateway />
            </MainLayout>
          }
        />

        <Route
          path="/login"
          element={
            <MainLayout>
              <AdminLogin />
            </MainLayout>
          }
        />

        <Route
          path="/about-us"
          element={
            <MainLayout>
              <AboutUs />
            </MainLayout>
          }
        />

        <Route
          path="/news"
          element={
            <MainLayout>
              <NewsList />
            </MainLayout>
          }
        />

        <Route
          path="/news/:id"
          element={
            <MainLayout>
              <NewsDetail />
            </MainLayout>
          }
        />

        <Route
          path="/services/cruise"
          element={
            <MainLayout>
              <Cruise />
            </MainLayout>
          }
        />

        <Route
          path="/services/employment"
          element={
            <MainLayout>
              <EmploymentMain />
            </MainLayout>
          }
        />

        <Route
          path="/contact-us"
          element={
            <MainLayout>
              <ContactUs />
            </MainLayout>
          }
        />

        <Route
          path="/faq"
          element={
            <MainLayout>
              <QnA />
            </MainLayout>
          }
        />

        <Route
          path="/inquiry"
          element={
            <MainLayout>
              <Inquiry />
            </MainLayout>
          }
        />

        <Route
          path="/inquiry/:id"
          element={
            <MainLayout>
              <InquiryDetail />
            </MainLayout>
          }
        />

        <Route
          path="/inquiry/edit/:id"
          element={
            <MainLayout>
              <InquiryEdit />
            </MainLayout>
          }
        />

        <Route
          path="/inquiry/new"
          element={
            <MainLayout>
              <InquiryWrite />
            </MainLayout>
          }
        />

        <Route
          path="/employment"
          element={
            <MainLayout>
              <Employment />
            </MainLayout>
          }
        />
        <Route
          path="/employment/:id"
          element={
            <MainLayout>
              <EmploymentDetail />
            </MainLayout>
          }
        />
        <Route
          path="/employment/:id/inquiry"
          element={
            <MainLayout>
              <EmploymentInquiry />
            </MainLayout>
          }
        />

        <Route
          path="/admin"
          element={
            <ProtectedAdminRoute>
              <AdminDashboard />
            </ProtectedAdminRoute>
          }
        />

        <Route
          path="/admin/news/list"
          element={
            <ProtectedAdminRoute>
              <AdminNewsList />
            </ProtectedAdminRoute>
          }
        />
        <Route
          path="/admin/news/new"
          element={
            <ProtectedAdminRoute>
              <NewsCreate />
            </ProtectedAdminRoute>
          }
        />
        <Route
          path="/admin/news/:id"
          element={
            <ProtectedAdminRoute>
              <AdminNewsDetail />
            </ProtectedAdminRoute>
          }
        />

        <Route
          path="/admin/employment/list"
          element={
            <ProtectedAdminRoute>
              <AdminEmploymentList />
            </ProtectedAdminRoute>
          }
        />
        <Route
          path="/admin/employment/:id"
          element={
            <ProtectedAdminRoute>
              <AdminEmploymentDetail />
            </ProtectedAdminRoute>
          }
        />
        <Route
          path="/admin/employment/new"
          element={
            <ProtectedAdminRoute>
              <EmploymentCreate />
            </ProtectedAdminRoute>
          }
        />
        <Route
          path="/admin/employment/inquiry/list"
          element={
            <ProtectedAdminRoute>
              <EmploymentInquiryList />
            </ProtectedAdminRoute>
          }
        />
        <Route
          path="/admin/employment/inquiry/:id"
          element={
            <ProtectedAdminRoute>
              <EmploymentInquiryDetail />
            </ProtectedAdminRoute>
          }
        />

        <Route
          path="/admin/user/register"
          element={
            <ProtectedAdminRoute>
              <AdminLayout>
                <AdminRegister />
              </AdminLayout>
            </ProtectedAdminRoute>
          }
        />

        <Route
          path="/admin/user/list"
          element={
            <ProtectedAdminRoute>
              <UserList />
            </ProtectedAdminRoute>
          }
        />

        <Route
          path="/admin/users/:id"
          element={
            <ProtectedAdminRoute>
              <UserDetail />
            </ProtectedAdminRoute>
          }
        />

        <Route
          path="/admin/contact/list"
          element={
            <ProtectedAdminRoute>
              <ContactList />
            </ProtectedAdminRoute>
          }
        />

        <Route
          path="/admin/contact/:id"
          element={
            <ProtectedAdminRoute>
              <ContactDetail />
            </ProtectedAdminRoute>
          }
        />

        <Route
          path="/admin/news/new"
          element={
            <ProtectedAdminRoute>
              <NewsCreate />
            </ProtectedAdminRoute>
          }
        />

        <Route
          path="*"
          element={
            <MainLayout>
              <NotFound />
            </MainLayout>
          }
        />
      </Routes>
    </>
  );
}
